import { AlertDialog as AlertDialogPrimitive } from "@base-ui/react/alert-dialog"
import { PortalContainerProvider } from "@src/hooks/usePortalContainer"
import clsx from "clsx"
import { type ReactNode, useState } from "react"

const AlertDialog = ({
  open,
  onCloseAnimationEnd,
  children,
}: {
  open: boolean
  onCloseAnimationEnd?: () => void
  children: ReactNode
}) => {
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(
    null
  )

  return (
    <AlertDialogPrimitive.Root
      open={open}
      onOpenChangeComplete={(nextOpen) => {
        if (!nextOpen) {
          onCloseAnimationEnd?.()
        }
      }}
    >
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Backdrop
          className={clsx(
            "fixed inset-0 z-10 bg-gray-900/80 transition-opacity duration-200 ease-out dark:bg-gray-900/90",

            "data-ending-style:opacity-0 data-starting-style:opacity-0 data-ending-style:duration-200 data-ending-style:ease-in"
          )}
        />

        <div className="fixed inset-0 z-20 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-2 pb-safe-offset-2 text-center sm:items-start sm:p-0 has-sidebar:xl:pl-74 [@media(min-width:640px)_and_(min-height:850px)]:items-start [@media(min-width:640px)_and_(min-height:850px)]:pt-[10dvh]">
            <AlertDialogPrimitive.Popup
              ref={setPortalContainer}
              className={clsx(
                "dark:-outline-offset-1 relative w-full max-w-sm transform overflow-hidden rounded-3xl bg-white p-5 text-left shadow-xl transition-all duration-300 ease-out sm:my-8 dark:bg-gray-850 dark:outline dark:outline-white/5",

                "data-starting-style:translate-y-4 data-starting-style:opacity-0 sm:data-starting-style:translate-y-0 sm:data-starting-style:scale-95",

                "data-ending-style:translate-y-4 data-ending-style:opacity-0 data-ending-style:duration-200 data-ending-style:ease-in sm:data-ending-style:translate-y-0 sm:data-ending-style:scale-95"
              )}
            >
              <PortalContainerProvider container={portalContainer}>
                {children}
              </PortalContainerProvider>
            </AlertDialogPrimitive.Popup>
          </div>
        </div>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  )
}

export default AlertDialog

const Title = ({
  children,
  className,
}: { children: ReactNode; className?: string }) => (
  <AlertDialogPrimitive.Title
    className={clsx(
      "text-center font-bold text-gray-900 text-xl/6 tracking-tight dark:text-white",
      className
    )}
  >
    {children}
  </AlertDialogPrimitive.Title>
)

const Description = ({
  children,
  className,
}: { children: ReactNode; className?: string }) => (
  <AlertDialogPrimitive.Description
    className={clsx(
      "text-balance text-center font-medium text-base/5 text-gray-500 dark:text-gray-400",
      className
    )}
  >
    {children}
  </AlertDialogPrimitive.Description>
)

AlertDialog.Title = Title
AlertDialog.Description = Description
